import { useState } from 'react';
import { useDispatch } from 'react-redux';
import TextInputField from 'components/TextInputField';
import ActionButton from 'components/ActionButton';
import StatusMessage from 'components/StatusMessage';
import { useRegisterMutation } from 'store/services/auth/authApi';
import { loginSuccess } from 'store/services/auth/authSlice';
import { meApi } from 'store/services/user/meApi';
import { haveListApi } from 'store/services/collectionList/haveList/haveListApi';
import { wantListApi } from 'store/services/collectionList/wantList/wantListApi';
import { tradeApi } from 'store/services/trade/tradeApi';
import { sanitizeUsername, sanitizeEmail } from 'utils/inputSanitization';
import { trackEvent, getUserProperties, trackSignupConversion } from 'utils/analytics';
import { getAttributionForSignup, clearAttribution } from 'utils/attribution';
import '../components/LoginForm/forms/JoinWaitlistTab/JoinWaitlistTab.css';

type Props = {
  email: string;
  onNext: () => void;
};

const PasswordStep = ({ email, onNext }: Props) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [register, { isLoading, error }] = useRegisterMutation();
  const dispatch = useDispatch();

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[@#$%^&*!]/.test(pwd)) {
      return 'Password must contain a special character (!@#$%^&*)';
    }
    return null;
  };

  const handleContinue = async () => {
    if (!email || !username || !password) return;

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      alert(passwordError);
      return;
    }

    // Sanitize inputs before registration
    // Include referral source for marketing attribution (stored by backend)
    // Supports both ?ref=partner and ?utm_source=partner URLs
    const referralSource = (() => {
      try {
        return localStorage.getItem('referral_source') || undefined;
      } catch {
        return undefined;
      }
    })();

    // Ad-click attribution (gclid/UTM) captured on landing; null when absent
    // or older than the 90-day TTL — undefined keeps the field out of the JSON
    const attribution = getAttributionForSignup() ?? undefined;

    const registrationData = {
      email: sanitizeEmail(email),
      username: sanitizeUsername(username),
      password, // Don't alter passwords
      referralSource, // Pass to backend for storage
      attribution,
    };
    
    try {
      // Clear all cached data before registering a new user
      dispatch(meApi.util.resetApiState());
      dispatch(haveListApi.util.resetApiState());
      dispatch(wantListApi.util.resetApiState());
      dispatch(tradeApi.util.resetApiState());
      
      const result = await register(registrationData).unwrap();
      
      if (result.token) {
        // Track signup completion in GA4
        trackEvent('signup_complete', getUserProperties({
          id: result.user.id,
          createdAt: result.user.createdAt,
          country: null, // Country not yet provided at signup
        }));

        // Report the signup to Google Ads. This is the same moment the admin
        // dashboard counts a new user (a User row created by /auth/beta-register),
        // so the two figures stay reconcilable. Guarded because analytics must
        // never break account creation.
        try {
          trackSignupConversion(result.user.email);
        } catch (conversionErr) {
          console.error('Signup conversion tracking failed (non-fatal):', conversionErr);
        }

        // Clear referral source after successful registration
        // Prevents it from persisting for future registrations
        try {
          localStorage.removeItem('referral_source');
        } catch {
          // localStorage not available
        }

        // Consume ad-click attribution the same way — a second account on
        // this browser must not re-claim the same ad click
        clearAttribution();

        // Auto-login the user with the returned token and user data
        dispatch(loginSuccess({
          token: result.token,
          user: result.user,
          csrfToken: (result as any).csrfToken // Include CSRF token if provided
        }));
        
        // Force refetch of user data after registration
        dispatch(meApi.util.invalidateTags(['User']));
        
        // Longer delay to ensure cookies are fully set before proceeding
        // This is critical for cookie authentication on staging/prod
        // Increased from 100ms to 500ms to handle cross-domain cookie propagation
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Continue to next step in onboarding (AddressStep)
        onNext();
      } else {
        console.error('❌ No token in registration response');
      }
    } catch (err: any) {
      console.error('❌ Registration failed:', err);
      console.error('Full error object:', JSON.stringify(err, null, 2));
      console.error('Error details:', {
        status: err?.status,
        data: err?.data,
        message: err?.data?.message,
        errors: err?.data?.errors
      });
      // Error is handled by the error state from the mutation
    }
  };

  const getErrorMessage = (error: any) => {
    if (!error?.data) return 'Registration failed. Please try again.';
    
    if (error.data.message) return error.data.message;
    
    if (error.data.errors && typeof error.data.errors === 'object') {
      try {
        return Object.values(error.data.errors).join(', ');
      } catch {
        return 'Registration failed. Please try again.';
      }
    }
    
    return 'Registration failed. Please try again.';
  };

  const renderPasswordForm = () => (
    <>
      <TextInputField
        label="Username"
        value={username}
        onChange={setUsername}
      />
      <TextInputField
        label="Password"
        value={password}
        onChange={setPassword}
        type="password"
      />
      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
        Password must be 8+ characters with uppercase, lowercase, number, and special character (!@#$%^&*)
      </div>
      <ActionButton
        label="Create Account"
        onPress={handleContinue}
        isLoading={isLoading}
        isDisabled={!password || !email || !username}
      />
      {error && (
        <StatusMessage 
          type="error" 
          message={getErrorMessage(error)} 
        />
      )}
    </>
  );

  return (
    <div className="login-form">
      <div className="login-form-content join-waitlist">
        <div className="onboarding-progress-dots">
          <div className="dot" />
          <div className="dot active" />
          <div className="dot" />
          <div className="dot" />
        </div>

        <h1 className="join-waitlist-title">Create your account</h1>
        {email && (
          <p className="join-waitlist-description">
            Creating account for: <strong>{email}</strong>
          </p>
        )}

        <div className="join-waitlist-input">
          {renderPasswordForm()}
        </div>
      </div>
    </div>
  );
};

export default PasswordStep;
